// Reference data: small, global-ish lookup tables used to label and configure
// the dashboard. Cached aggressively by TanStack Query in the routes.
import { supabase } from "@/lib/supabase";
import { getActiveOrgId } from "@/lib/active-org";
import type { Brand, Platform, MetricDef, Benchmark, DataSource, Market, FxRate } from "@/lib/api/types";

export async function getMarkets(): Promise<Market[]> {
  const { data, error } = await supabase
    .from("markets")
    .select("region, name, currency, sort_order")
    .order("sort_order");
  if (error) throw error;
  return (data ?? []) as Market[];
}

export async function getFxRates(): Promise<FxRate[]> {
  const { data, error } = await supabase
    .from("fx_rates")
    .select("currency, to_eur, symbol");
  if (error) throw error;
  return (data ?? []) as FxRate[];
}

export async function getBrands(): Promise<Brand[]> {
  const { data, error } = await supabase
    .from("brands")
    .select("id, key, name, home_region, accent_color")
    .order("name");
  if (error) throw error;
  return (data ?? []) as Brand[];
}

export async function getPlatforms(): Promise<Platform[]> {
  const { data, error } = await supabase
    .from("platforms")
    .select("id, key, name, category, kind, is_aggregator, supports_direct, accent_color, sort_order")
    .order("sort_order");
  if (error) throw error;
  return (data ?? []) as Platform[];
}

export async function getMetricCatalog(): Promise<MetricDef[]> {
  const { data, error } = await supabase
    .from("metric_catalog")
    .select("key, label, short_label, unit, activity_scope, higher_is_better, description, sort_order")
    .order("sort_order");
  if (error) throw error;
  return (data ?? []) as MetricDef[];
}

export async function getBenchmarks(): Promise<Benchmark[]> {
  const { data, error } = await supabase
    .from("benchmarks")
    .select("id, metric_key, activity_type, region, brand_id, target_value, period, note");
  if (error) throw error;
  return (data ?? []) as Benchmark[];
}

/** Update a single benchmark's target value (edit mode). */
export async function updateBenchmarkTarget(id: string, target_value: number): Promise<void> {
  const { error } = await supabase.from("benchmarks").update({ target_value }).eq("id", id);
  if (error) throw error;
}

/** Create-or-update a benchmark by its natural key (org + metric + activity +
 *  region + brand). org_id is resolved from the active-org singleton. */
export async function upsertBenchmark(b: {
  metric_key: string;
  activity_type: string;
  region: string | null;
  brand_id: string | null;
  target_value: number;
  period?: string;
  note?: string;
}): Promise<void> {
  const org_id = getActiveOrgId();
  if (!org_id) throw new Error("Geen actieve organisatie");
  const { error } = await supabase
    .from("benchmarks")
    .upsert(
      { period: "monthly", note: "", org_id, ...b },
      { onConflict: "org_id,metric_key,activity_type,region,brand_id" },
    );
  if (error) throw error;
}

// Which (region, brand, channel) combinations each market actually uses. Driven
// by the configured account_map (plus anything with data), so a channel that is
// set up but currently has no rows — Pinterest while its token is reconnected —
// stays selectable. Used to hide impossible selections (a US-only brand under EU).
export interface Availability {
  region: string;
  brand_id: string;
  platform_id: string;
  /** ecom | awareness | job_ads — null for account_map rows without one. */
  activity_type: string | null;
}

export async function getAvailability(): Promise<Availability[]> {
  const { data, error } = await supabase
    .from("brand_channel_availability")
    .select("region, brand_id, platform_id, activity_type");
  if (error) throw error;
  return (data ?? []) as Availability[];
}

export async function getDataSources(): Promise<DataSource[]> {
  const { data, error } = await supabase
    .from("data_sources")
    .select("id, platform_id, brand_id, status, method, last_synced_at, rows_ingested, note");
  if (error) throw error;
  return (data ?? []) as DataSource[];
}

// Secret-free view of each connection's token/re-auth deadline (see
// migration 0012). Drives the "Verbindingen & houdbaarheid" panel so
// connections get reconnected before they lapse.
export interface ConnectionHealth {
  platform_key: string;
  platform_name: string;
  status: string;
  last_sync_at: string | null;
  authorized_at: string | null;
  token_kind: string;
  owner: string;
  reconnect_path: string | null;
  policy_notes: string | null;
  /** Set when the data does not come from the platform's own connection —
   *  today only Pinterest, which is borrowed from Converge until its app is
   *  approved for Standard access. */
  source_note: string | null;
  reauth_by: string | null;
  health: "ok" | "expiring" | "expired" | "error" | "bridged";
  days_left: number | null;
}

export async function getConnectionHealth(): Promise<ConnectionHealth[]> {
  const { data, error } = await supabase
    .from("connection_health")
    .select(
      "platform_key, platform_name, status, last_sync_at, authorized_at, token_kind, owner, reconnect_path, policy_notes, source_note, reauth_by, health, days_left",
    );
  if (error) throw error;
  return (data ?? []) as ConnectionHealth[];
}
